import {
    useRef,
    useEffect,
    useState,
    forwardRef,
    useImperativeHandle,
} from 'react';
import useAnimatedText from '~/lib/useAnimatedText.js';
import SparklesIcon from '~/components/icons/SparklesIcon.jsx';
import { useBusinessInfo } from '~/lib/Queries';
import { ArrowUpIcon } from '@heroicons/react/24/outline';
import { StopIcon } from '@heroicons/react/24/solid';
import AssistantVoiceInput from '~/components/assistant/AssistantVoiceInput.jsx';

const chatShowingPlaceholders = [
    'Help me with...',
    'I need help setting up...',
    'Help me set up...',
    'I need help with...',
];

const chatNewBusinessPlaceholders = [
    "Describe your business and what you're looking to set up...",
    "Set up my account based on my website https://example.com",
];

const chatHiddenPlaceholders = [
    'Need help setting up availability?',
    'Can I help create forms or automations for you?',
    'Want me to create some event types for you?',
    'Wondering how to grow your business?',
    'What can I help set up for you?',
];

const AssistantInput = forwardRef(
    (
        {
            prompt,
            setPrompt,
            isRunning,
            submit,
            onStop,
            onFocus,
            showChat,
            className,
            isNewBusiness,
            placeholders,
        },
        ref,
    ) => {
        const textareaRef = useRef(null);
        const [placeholderIndex, setPlaceholderIndex] = useState(0);

        // Get business info, if business is <14 days old then isYoungBusiness is true
        const { data: businessData } = useBusinessInfo();
        const isYoungBusiness = businessData?.business?.created_at
            ? (new Date() - new Date(businessData?.business?.created_at)) / (1000 * 60 * 60 * 24) < 14
            : false;

        // Expose focus method via ref
        useImperativeHandle(
            ref,
            () => ({
                focus() {
                    textareaRef.current?.focus();
                },
            }),
            [],
        );

        let placeholderOption = 'Help me with...';

        if (showChat && placeholders?.length) {
            // A page with its own kind of request can say what to suggest
            placeholderOption =
                placeholders[placeholderIndex % placeholders.length];
        } else if (showChat) {
            placeholderOption = isNewBusiness
                ? chatNewBusinessPlaceholders[
                      placeholderIndex % chatNewBusinessPlaceholders.length
                  ]
                : chatShowingPlaceholders[
                      placeholderIndex % chatShowingPlaceholders.length
                  ];
        } else if (isYoungBusiness) {
            // Only rotate through hidden placeholders if business is young to draw attention
            //  for older businesses this is distracting
            placeholderOption =
                chatHiddenPlaceholders[
                    placeholderIndex % chatHiddenPlaceholders.length
                ];
        }

        const placeholder = useAnimatedText(placeholderOption);

        const handleKeyDown = e => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit(e);
            }
        };

        useEffect(() => {
            setTimeout(() => {
                if (showChat) {
                    textareaRef.current?.focus();
                }
            }, 100);
        }, [isRunning]);

        useEffect(() => {
            const interval = setInterval(() => {
                setPlaceholderIndex(old => old + 1);
            }, 10000);

            return () => clearInterval(interval);
        }, []);

        // Auto-resize textarea based on content
        const resizeTextarea = () => {
            const textarea = textareaRef.current;
            if (!textarea) return;

            // Reset height to calculate proper scrollHeight
            textarea.style.height = 'auto';

            // Set new height based on content (with min/max constraints applied by CSS)
            let newHeight = Math.min(Math.max(textarea.scrollHeight, 48), 168); // 3em to 10.5em
            if (!showChat) {
                newHeight = Math.min(newHeight, 36);
            }
            textarea.style.height = `${newHeight}px`;
        };

        // Resize on content change
        useEffect(() => {
            resizeTextarea();
        }, [prompt]);

        // Initial resize
        useEffect(() => {
            resizeTextarea();
        }, [showChat]);

        return (
            <form
                onSubmit={submit}
                className={`flex items-center relative ${className}`}
            >
                {!showChat && (
                    <SparklesIcon className="w-5 h-5 absolute text-primary-700 left-4 top-3" />
                )}
                <div
                    className={`w-full ${!showChat ? 'pl-6' : ''} ${isRunning ? 'opacity-50' : ''}`}
                >
                    <textarea
                        ref={textareaRef}
                        placeholder={isRunning ? '' : placeholder}
                        value={prompt}
                        onChange={e => setPrompt(e.target.value)}
                        onFocus={e => {
                            resizeTextarea();
                            if (onFocus) onFocus(e);
                        }}
                        autoFocus={showChat}
                        onKeyDown={handleKeyDown}
                        className={`w-full pr-28 !max-w-full ${showChat ? 'min-h-[150px] bg-[#F8FAFF] border border-[#EAEDF3] focus:!border-gray-400' : ''} md:min-h-0 p-4 text-[#626976] rounded-[22px] resize-none overflow-hidden ${!showChat ? '!p-2 border-transparent' : ''}`}
                        rows={2}
                        disabled={isRunning}
                    />
                </div>
                {showChat && (
                    <div className="absolute bottom-3 right-2 flex">
                        <AssistantVoiceInput
                            prompt={prompt}
                            setPrompt={setPrompt}
                            onStartListening={() => {
                                textareaRef.current?.focus();
                            }}
                        />
                        {isRunning ? (
                            <button
                                type="button"
                                onClick={onStop}
                                className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-700 hover:bg-primary-800 text-white"
                                aria-label="Stop"
                            >
                                <StopIcon className="w-5 h-5" title="Stop" />
                            </button>
                        ) : (
                            <button
                                type="submit"
                                className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-700 hover:bg-primary-800 text-white disabled:opacity-50"
                                disabled={!(prompt||'').trim()}
                                aria-label="Submit"
                            >
                                <ArrowUpIcon className="w-5 h-5 stroke-2" />
                            </button>
                        )}
                    </div>

                )}
            </form>
        );
    },
);

export default AssistantInput;
